import { Box, Button, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import useWindowSize from "../../../hooks/useWindowSize";
import { BREAKPOINT, trackClick } from "../../_helpers";

export function ContactSection(props) {
  const { artistData } = props;
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    trackClick("contact-submit", { label: "Contact form submit", section: "contact" });
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit to Netlify Forms
      const formElement = e.target;
      const data = new FormData(formElement);

      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as any).toString(),
      });

      console.log("Form submitted successfully to Netlify");
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      color: "var(--color5)",
      fontFamily: "Poppins",
      fontSize: { xs: "2.1rem", md: "1.2rem" },
      backgroundColor: "var(--logoColor2)",
      borderRadius: { xs: "0.875rem", md: "0.78rem" },
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        borderColor: "var(--logoColor)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--logoColor)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "var(--color5)",
      fontFamily: "Poppins",
      fontSize: { xs: "2.1rem", md: "1.2rem" },
      fontWeight: "600",
      backgroundColor: "var(--logoColor2)",
      padding: "0 0.5rem",
      borderRadius: "0.25rem",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "var(--color5)",
      backgroundColor: "var(--logoColor2)",
    },
  };

  return (
    <Box
      className="ContactSection"
      sx={{
        position: "relative",
        width: `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: { xs: "4rem 2rem", md: "3.5rem" },
        m: { xs: "1rem 2.5rem 0", md: "12.25rem 2.5rem 0" },
        mb: { xs: "8rem", md: "10rem" },
        backgroundColor: "var(--color5)",
        borderRadius: "1.875rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: { xs: "2.5rem", md: "2rem" },
        zIndex: 1,
      }}
    >
      <Typography
        className="LetSTalk"
        sx={{
          color: "var(--logoColor2)",
          fontSize: { xs: "3rem", md: "3.34rem" },
          fontFamily: "Hanson",
          fontWeight: "700",
        }}
      >
        LET'S TALK
      </Typography>

      {reallyIsMobile && !isFormExpanded && (
        <Button
          onClick={() => {
            trackClick("contact-open-form", { label: "Open contact form", section: "contact" });
            setIsFormExpanded(true);
          }}
          sx={{
            width: "100%",
            padding: "1.25rem",
            backgroundColor: "var(--logoColor2)",
            color: "var(--color5)",
            fontSize: "2rem",
            fontFamily: "Hanson",
            fontWeight: "700",
            borderRadius: "0.75rem",
            textTransform: "uppercase",
            "&:hover": {
              backgroundColor: "var(--logoColor)",
            },
          }}
        >
          Open contact form
        </Button>
      )}

      {(!reallyIsMobile || isFormExpanded) && (
        <Box
          component="form"
          name="contact"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="ContactForm"
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: { xs: "1.75rem", md: "1.5rem" },
          }}
        >
        {/* Netlify form fields */}
        <input type="hidden" name="form-name" value="contact" />
        <input type="hidden" name="bot-field" />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "3rem", md: "1.5rem" },
          }}
        >
          <TextField
            fullWidth
            label="What's your name?"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            sx={{ ...inputStyles, flex: 1 }}
            InputProps={{
              sx: {
                padding: { xs: "1.5rem 2rem", md: "1rem 1.3rem" },
              },
            }}
          />
          <TextField
            fullWidth
            label="Email address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            sx={{ ...inputStyles, flex: 1 }}
            InputProps={{
              sx: {
                padding: { xs: "1.5rem 2rem", md: "1rem 1.3rem" },
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          label="What are you contacting me about?"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          sx={inputStyles}
          InputProps={{
            sx: {
              padding: { xs: "1.5rem 2rem", md: "1rem 1.3rem" },
            },
          }}
        />

        <TextField
          fullWidth
          label="Your message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          multiline
          rows={6}
          sx={inputStyles}
          InputProps={{
            sx: {
              padding: { xs: "1.5rem 2rem", md: "1rem 1.3rem" },
            },
          }}
        />

        {submitStatus === "success" && (
          <Typography
            sx={{
              color: "var(--logoColor2)",
              fontSize: { xs: "1rem", md: "1rem" },
              fontFamily: "Poppins",
              fontWeight: "600",
              textAlign: "center",
              padding: { xs: "1rem", md: "1rem" },
              backgroundColor: "rgba(0, 255, 0, 0.1)",
              borderRadius: { xs: "0.5rem", md: "0.5rem" },
            }}
          >
            Thank you! Your message has been sent successfully.
          </Typography>
        )}

        {submitStatus === "error" && (
          <Typography
            sx={{
              color: "#ff4444",
              fontSize: { xs: "1rem", md: "1rem" },
              fontFamily: "Poppins",
              fontWeight: "600",
              textAlign: "center",
              padding: { xs: "1rem", md: "1rem" },
              backgroundColor: "rgba(255, 0, 0, 0.1)",
              borderRadius: { xs: "0.5rem", md: "0.5rem" },
            }}
          >
            Something went wrong. Please try again later.
          </Typography>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          sx={{
            width: "100%",
            padding: { xs: "1.25rem", md: "1.625rem" },
            backgroundColor: "var(--logoColor2)",
            color: "var(--color5)",
            fontSize: { xs: "2rem", md: "1.46rem" },
            fontFamily: "Hanson",
            fontWeight: "700",
            borderRadius: { xs: "0.75rem", md: "0.78rem" },
            textTransform: "uppercase",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "var(--logoColor)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              backgroundColor: "var(--color4)",
              color: "var(--logoColor)",
              opacity: 0.6,
            },
          }}
        >
          {isSubmitting ? "SENDING..." : "SEND MESSAGE"}
        </Button>
        </Box>
      )}

      <Box
        sx={{
          width: { xs: "90%", md: "80%" },
          height: "1px",
          backgroundColor: "var(--logoColor2)",
          opacity: 0.3,
          margin: { xs: "2rem auto 0", md: "1rem auto 0" },
        }}
      />

      <Typography
        sx={{
          width: "100%",
          color: "var(--logoColor)",
          fontSize: { xs: "2.1rem", md: "1.2rem" },
          fontFamily: "Poppins",
          fontWeight: "400",
          lineHeight: 1.9,
          textAlign: "center",
          mt: { xs: "1rem", md: "1rem" },
        }}
      >
        Have a question, collaboration idea, or just want to say hi? Drop me a message and I'll get back to you as soon as possible.
      </Typography>
    </Box>
  );
}
